// Manager-only flagged-reconciliation queue. GET /api/v1/ops/recon/flagged
// + POST resolve. Editorial table matching /clients.

import { useCallback, useEffect, useState } from 'react';

import { Kicker } from '../../components/shell';
import { api } from '../../lib/api';

interface ReconRow {
  id: string;
  kind: 'cash' | 'asset';
  account: string;
  account_label: string;
  client_id: string;
  client_name: string;
  as_of: string;
  // Cash-only fields.
  expected_cash_usd?: string;
  observed_cash_usd?: string;
  // Asset-only fields.
  asset?: string;
  expected_qty?: string;
  observed_qty?: string;
  delta_qty?: string;
  // Common fields.
  delta_usd: string;
  tolerance_usd: string;
  status: string;
  notes: string;
  created_at: string;
  resolved_at: string | null;
  resolved_by_email: string | null;
}

const usd = (s: string | undefined) =>
  s == null ? '—' : Number(s).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });

const qty = (s: string | undefined) =>
  s == null ? '—' : Number(s).toLocaleString('en-US', { maximumFractionDigits: 8 });

export default function Recon() {
  const [rows, setRows] = useState<ReconRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [resolving, setResolving] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setRows(await api<ReconRow[]>('/ops/recon/flagged'));
    } catch {
      setError('Failed to load flagged reconciliations.');
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const resolve = async (id: string) => {
    const note = window.prompt(
      'Resolution note (what was the cause? what corrective tx was posted?)',
      '',
    );
    if (note === null) return; // cancelled
    setResolving(id);
    try {
      await api(`/ops/recon/${id}/resolve`, { method: 'POST', body: { note } });
      await load();
    } catch {
      setError('Failed to resolve. Try again.');
    } finally {
      setResolving(null);
    }
  };

  return (
    <div>
      <div className="section-head" style={{ marginBottom: 32 }}>
        <Kicker>02 · Reconciliation · Flagged queue</Kicker>
        <h1 className="page-title">Records outside <em>tolerance.</em></h1>
        <p className="serif-body" style={{ maxWidth: '56ch', marginTop: 12, color: 'var(--ink-2)' }}>
          Each row is an account whose observed cash balance diverges from the
          equation by more than its tolerance band. Investigate, post a
          corrective adjustment, and resolve.
        </p>
      </div>

      {error && (
        <p style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', color: 'var(--accent)' }}>
          {error}
        </p>
      )}

      {!rows && !error && (
        <p style={{
          fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.16em',
          textTransform: 'uppercase', color: 'var(--ink-3)',
        }}>
          Loading…
        </p>
      )}

      {rows && rows.length === 0 && (
        <p style={{ fontFamily: 'var(--serif)', color: 'var(--ink-2)' }}>
          No flagged records. Every account is within tolerance.
        </p>
      )}

      {rows && rows.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--ink)' }}>
              <Th>Kind</Th>
              <Th>Client / account</Th>
              <Th>As of</Th>
              <Th align="right">Expected</Th>
              <Th align="right">Observed</Th>
              <Th align="right">Δ</Th>
              <Th align="right">Tolerance</Th>
              <Th align="right">Action</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const isAsset = r.kind === 'asset';
              const expected = isAsset
                ? `${qty(r.expected_qty)} ${r.asset ?? ''}`
                : usd(r.expected_cash_usd);
              const observed = isAsset
                ? `${qty(r.observed_qty)} ${r.asset ?? ''}`
                : usd(r.observed_cash_usd);
              return (
                <tr key={r.id} style={{ borderBottom: 'var(--hairline)' }}>
                  <Td>
                    <span style={{
                      fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.14em',
                      textTransform: 'uppercase',
                      color: isAsset ? 'var(--accent)' : 'var(--ink-3)',
                      border: `0.5pt solid ${isAsset ? 'var(--accent)' : 'var(--ink-3)'}`,
                      padding: '2px 6px',
                    }}>
                      {r.kind}{isAsset && r.asset ? ` · ${r.asset}` : ''}
                    </span>
                  </Td>
                  <Td>
                    <span style={{ fontFamily: 'var(--serif)', fontSize: 18 }}>
                      <em>{r.client_name}</em>
                    </span>
                    <div style={{
                      fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.14em',
                      textTransform: 'uppercase', color: 'var(--ink-3)', marginTop: 4,
                    }}>
                      {r.account_label}
                    </div>
                  </Td>
                  <Td mono>
                    {new Date(r.as_of).toISOString().slice(0, 16).replace('T', ' ')} UTC
                  </Td>
                  <Td align="right" mono>{expected}</Td>
                  <Td align="right" mono>{observed}</Td>
                  <Td align="right" mono>
                    <span style={{ color: 'var(--accent)', fontStyle: 'italic' }}>
                      {Number(r.delta_usd) > 0 ? '+' : ''}{usd(r.delta_usd)}
                    </span>
                  </Td>
                  <Td align="right" mono>{usd(r.tolerance_usd)}</Td>
                  <Td align="right">
                    <button
                      className="btn-link"
                      onClick={() => void resolve(r.id)}
                      disabled={resolving === r.id}
                    >
                      {resolving === r.id ? 'Resolving…' : 'Resolve →'}
                    </button>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function Th({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return (
    <th style={{
      textAlign: align, padding: '12px 16px',
      fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.16em',
      textTransform: 'uppercase', color: 'var(--ink-3)', fontWeight: 400,
    }}>
      {children}
    </th>
  );
}

function Td({
  children, align = 'left', mono = false,
}: { children: React.ReactNode; align?: 'left' | 'right'; mono?: boolean }) {
  return (
    <td style={{
      textAlign: align, padding: '20px 16px',
      fontFamily: mono ? 'var(--mono)' : 'inherit',
      fontVariantNumeric: mono ? 'tabular-nums' : 'normal',
      fontSize: mono ? 14 : undefined,
      verticalAlign: 'top',
    }}>
      {children}
    </td>
  );
}
